import { useMemo, useState } from "react";
import { PageHeader, Card, Button, Badge } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import { usePermissions } from "@/hooks/usePermissions";
import { toast } from "@/state/toast";
import {
  useAiCostSummary,
  useAiCostPricing,
  useUpdatePricing,
  type CostStatus,
  type PricingRow,
} from "@/hooks/useAiCost";

const PRESETS: { label: string; days: number }[] = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

const money = (usd: number) => {
  if (usd === 0) return "$0.00";
  const decimals = usd < 1 ? 4 : 2;
  return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: decimals })}`;
};

const num = (n: number) => n.toLocaleString();

const STATUS_BADGE: Record<CostStatus, { variant: "success" | "warning" | "default"; label: string }> = {
  measured: { variant: "success", label: "Measured" },
  approximate: { variant: "warning", label: "Approximate" },
  not_instrumented: { variant: "default", label: "Not tracked" },
};

const CONTENT_TYPE_LABEL: Record<string, string> = {
  product_caption: "Product caption",
  motivational_nudge: "Motivational nudge",
  voice_message: "Voice message",
  gamified_prompt: "Gamified prompt",
};

const AiCost = () => {
  const { isSuperadmin } = usePermissions();

  const [presetDays, setPresetDays] = useState(30);
  const { from, to } = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getTime() - presetDays * 24 * 60 * 60 * 1000);
    return { from: isoDay(start), to: isoDay(now) };
  }, [presetDays]);

  const summaryQuery = useAiCostSummary(from, to);
  const pricingQuery = useAiCostPricing();
  const updatePricing = useUpdatePricing();

  const [edits, setEdits] = useState<Record<string, { input: string; output: string }>>({});

  if (!isSuperadmin) {
    return (
      <div className="page">
        <PageHeader label="Administration" title="AI Cost" description="Platform AI spend across all usage." />
        <Card>
          <div className="empty">
            <div className="empty-i"><Icon name="shield" /></div>
            <b>Superadmin only</b>
            <p>AI cost data is restricted to superadmins.</p>
          </div>
        </Card>
      </div>
    );
  }

  const summary = summaryQuery.data;

  const startEdit = (row: PricingRow) => {
    setEdits((e) => ({
      ...e,
      [row.model_key]: {
        input: String(row.input_usd),
        output: String(row.output_usd),
      },
    }));
  };

  const saveEdit = async (row: PricingRow) => {
    const edit = edits[row.model_key];
    if (!edit) return;
    const input_usd = parseFloat(edit.input);
    const output_usd = parseFloat(edit.output);
    if (Number.isNaN(input_usd) || Number.isNaN(output_usd)) {
      toast("Enter valid numbers");
      return;
    }
    try {
      await updatePricing.mutateAsync({ model_key: row.model_key, input_usd, output_usd });
      setEdits((e) => {
        const next = { ...e };
        delete next[row.model_key];
        return next;
      });
      toast(`Updated ${row.model_key} pricing`);
    } catch {
      toast("Failed to update pricing");
    }
  };

  return (
    <div className="page wide">
      <PageHeader
        label="Administration"
        title="AI Cost"
        description="Original platform AI spend, computed from real token usage against editable per-model pricing."
        actions={
          <div className="seg">
            {PRESETS.map((p) => (
              <button key={p.days} className={presetDays === p.days ? "on" : ""} onClick={() => setPresetDays(p.days)}>
                {p.label}
              </button>
            ))}
          </div>
        }
      />

      {summaryQuery.isLoading ? (
        <Card><div className="empty sm"><p>Loading cost data…</p></div></Card>
      ) : summaryQuery.isError ? (
        <Card>
          <div className="al e">
            <Icon name="alert" />
            <div className="cnt-x">Failed to load cost data. The AI API must be running with the cost endpoint deployed.</div>
          </div>
        </Card>
      ) : summary ? (
        <>
          {/* KPI cards */}
          <div className="stats">
            <div className="stat">
              <div className="stat-h">
                <span className="stat-i g"><Icon name="zap" /></span>
                <span className="lb">Total Measured Cost</span>
              </div>
              <div className="stat-v">{money(summary.total_measured_usd)}</div>
              <div className="stat-b"><span className="sl">Last {presetDays} days · USD</span></div>
            </div>
            <div className="stat">
              <div className="stat-h">
                <span className="stat-i b"><Icon name="tmpl" /></span>
                <span className="lb">Content Generation</span>
              </div>
              <div className="stat-v">{money(summary.text_cost_usd)}</div>
              <div className="stat-b"><span className="sl">{num(summary.totals.generations)} generations</span></div>
            </div>
            <div className="stat">
              <div className="stat-h">
                <span className="stat-i a"><Icon name="mic" /></span>
                <span className="lb">Voice (approx)</span>
              </div>
              <div className="stat-v">{money(summary.voice_cost_usd)}</div>
              <div className="stat-b"><span className="sl">{num(summary.totals.voice_chars_est)} chars · {summary.totals.voice_clips} clips</span></div>
            </div>
            <div className="stat">
              <div className="stat-h">
                <span className="stat-i p"><Icon name="act" /></span>
                <span className="lb">Tokens Processed</span>
              </div>
              <div className="stat-v" style={{ fontSize: 22 }}>
                {num(summary.totals.tokens_prompt + summary.totals.tokens_output)}
              </div>
              <div className="stat-b"><span className="sl">{num(summary.totals.tokens_prompt)} in · {num(summary.totals.tokens_output)} out</span></div>
            </div>
          </div>

          {/* Coverage callout */}
          {summary.not_instrumented.length > 0 ? (
            <div className="al w" style={{ marginBottom: 16 }}>
              <Icon name="info" />
              <div className="cnt-x">
                <b>No {summary.not_instrumented.map((f) => (f === "chat_assistant" ? "chat" : "embedding")).join(" or ")} usage
                recorded in this range yet.</b> Token capture is now enabled, so new chat turns and document ingestions are
                measured going forward — historical usage before capture was turned on cannot be backfilled. Voice remains an
                approximation (exact characters aren&apos;t recorded).
              </div>
            </div>
          ) : (
            <div className="al i" style={{ marginBottom: 16 }}>
              <Icon name="check" />
              <div className="cnt-x">
                <b>All AI surfaces instrumented.</b> Chat and embeddings are exact (from captured token usage); content
                generation is exact; voice is an approximation. Cost from before capture was enabled isn&apos;t included.
              </div>
            </div>
          )}

          {summary.missing_pricing.length > 0 && (
            <div className="al e" style={{ marginBottom: 16 }}>
              <Icon name="alert" />
              <div className="cnt-x">
                No price set for: <b>{summary.missing_pricing.join(", ")}</b>. These models appear in usage but are
                costed at $0 until you set a price below.
              </div>
            </div>
          )}

          {/* By feature */}
          <Card title="Cost by feature" description="What costs what across the platform.">
            <div className="tw">
              <table>
                <thead>
                  <tr><th>Feature</th><th>Status</th><th style={{ textAlign: "right" }}>Cost (USD)</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  {summary.by_feature.map((f) => {
                    const badge = STATUS_BADGE[f.status];
                    return (
                      <tr key={f.feature}>
                        <td><span className="pri-cell">{f.label}</span></td>
                        <td><Badge variant={badge.variant}>{badge.label}</Badge></td>
                        <td style={{ textAlign: "right" }} className="num">
                          {f.status === "not_instrumented" ? "—" : money(f.cost_usd)}
                        </td>
                        <td><span className="sub-cell" style={{ fontFamily: "inherit", whiteSpace: "normal" }}>{f.note}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="md c" style={{ marginTop: 16 }}>
            {/* By model */}
            <Card title="Cost by model" description="Content-generation token spend per model." noPadding>
              <div className="tw">
                <table>
                  <thead>
                    <tr><th>Model</th><th style={{ textAlign: "right" }}>Prompt</th><th style={{ textAlign: "right" }}>Output</th><th style={{ textAlign: "right" }}>Cost</th></tr>
                  </thead>
                  <tbody>
                    {summary.by_model.length === 0 ? (
                      <tr><td colSpan={4}><div className="empty sm"><p>No usage in range.</p></div></td></tr>
                    ) : summary.by_model.map((m) => (
                      <tr key={m.model}>
                        <td>
                          <span className="pri-cell mono">{m.model}</span>
                          {!m.priced && <span style={{ marginLeft: 6 }}><Badge variant="error">no price</Badge></span>}
                        </td>
                        <td style={{ textAlign: "right" }} className="num">{num(m.tokens_prompt)}</td>
                        <td style={{ textAlign: "right" }} className="num">{num(m.tokens_output)}</td>
                        <td style={{ textAlign: "right" }} className="num">{money(m.cost_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* By content type */}
            <Card title="Cost by content type" description="Content-generation spend per output type." noPadding>
              <div className="tw">
                <table>
                  <thead>
                    <tr><th>Type</th><th style={{ textAlign: "right" }}>Gens</th><th style={{ textAlign: "right" }}>Tokens</th><th style={{ textAlign: "right" }}>Cost</th></tr>
                  </thead>
                  <tbody>
                    {summary.by_content_type.length === 0 ? (
                      <tr><td colSpan={4}><div className="empty sm"><p>No usage in range.</p></div></td></tr>
                    ) : summary.by_content_type.map((t) => (
                      <tr key={t.content_type}>
                        <td><span className="pri-cell">{CONTENT_TYPE_LABEL[t.content_type] ?? t.content_type}</span></td>
                        <td style={{ textAlign: "right" }} className="num">{num(t.generations)}</td>
                        <td style={{ textAlign: "right" }} className="num">{num(t.tokens_prompt + t.tokens_output)}</td>
                        <td style={{ textAlign: "right" }} className="num">{money(t.cost_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </>
      ) : null}

      {/* Pricing editor */}
      <div style={{ marginTop: 16 }}>
      <Card
        title="Model pricing"
        description="USD per 1M tokens (chat / embedding) or per 1M characters (TTS). Update when provider prices change."
        noPadding
      >
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Model</th><th>Kind</th><th>Unit</th>
                <th style={{ textAlign: "right" }}>Input $</th>
                <th style={{ textAlign: "right" }}>Output $</th>
                <th>Updated</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pricingQuery.isLoading ? (
                <tr><td colSpan={7}><div className="empty sm"><p>Loading pricing…</p></div></td></tr>
              ) : (pricingQuery.data ?? []).map((row) => {
                const edit = edits[row.model_key];
                return (
                  <tr key={row.model_key}>
                    <td>
                      <span className="pri-cell mono">{row.model_key}</span>
                      {row.label && <span className="sub-cell" style={{ fontFamily: "inherit" }}>{row.label}</span>}
                    </td>
                    <td><Badge variant="default">{row.kind}</Badge></td>
                    <td><span className="sub-cell" style={{ fontFamily: "inherit" }}>{row.unit.replace(/_/g, " ")}</span></td>
                    <td style={{ textAlign: "right" }}>
                      {edit ? (
                        <input className="inp num" style={{ width: 90, textAlign: "right", height: 28 }} type="number" step="0.0001" min="0"
                          value={edit.input}
                          onChange={(e) => setEdits((s) => ({ ...s, [row.model_key]: { ...s[row.model_key], input: e.target.value } }))} />
                      ) : <span className="num">{row.input_usd.toFixed(4)}</span>}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {edit ? (
                        <input className="inp num" style={{ width: 90, textAlign: "right", height: 28 }} type="number" step="0.0001" min="0"
                          disabled={row.kind !== "chat"}
                          value={edit.output}
                          onChange={(e) => setEdits((s) => ({ ...s, [row.model_key]: { ...s[row.model_key], output: e.target.value } }))} />
                      ) : <span className="num">{row.kind === "chat" ? row.output_usd.toFixed(4) : "—"}</span>}
                    </td>
                    <td>
                      <span className="sub-cell" style={{ fontFamily: "inherit" }}>
                        {row.updated_by ? `${row.updated_by}` : "default"}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {edit ? (
                        <div className="acts" style={{ opacity: 1 }}>
                          <Button size="sm" onClick={() => saveEdit(row)} loading={updatePricing.isPending}>Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEdits((s) => { const n = { ...s }; delete n[row.model_key]; return n; })}>Cancel</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="secondary" onClick={() => startEdit(row)}>Edit</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      </div>
    </div>
  );
};

export default AiCost;
